/**
 * 简单 LRU 表：Map 迭代顺序即使用顺序（最近使用在前）。
 * 容量封顶由 trim() 显式触发，调用方决定哪些 key 受保护（例如当前展示的播放器）。
 */
export class LruPool<K, V> {
    /**
     * map，保存当前实例的map状态。
     */
    private map = new Map<K, V>();

    /**
     * 创建实例并初始化所需的领域状态。
     * @param maxSize maxSize参数，参与本次操作
     */
    constructor(private maxSize: number) {}

    /**
     * 读取当前size。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    get size() {
        return this.map.size;
    }

    /** 命中后刷新为最近使用 */
    get(key: K): V | undefined {
        if (!this.map.has(key)) {
            return undefined;
        }
        /**
         * value，供本模块相关规则使用。
         */
        const value = this.map.get(key) as V;
        this.map.delete(key);
        this.map.set(key, value);
        return value;
    }

    /**
     * has：执行对应的领域操作。
     * @param key key参数，参与本次操作
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    has(key: K): boolean {
        return this.map.has(key);
    }

    /** 查看条目但不刷新使用顺序 */
    peek(key: K): V | undefined {
        return this.map.get(key);
    }

    /**
     * set：执行对应的领域操作。
     * @param key key参数，参与本次操作
     * @param value 待处理的值
     */
    set(key: K, value: V) {
        this.map.delete(key);
        this.map.set(key, value);
    }

    /**
     * delete：执行对应的领域操作。
     * @param key key参数，参与本次操作
     */
    delete(key: K) {
        this.map.delete(key);
    }

    /**
     * clear：执行对应的领域操作。
     */
    clear() {
        this.map.clear();
    }

    /**
     * entries：执行对应的领域操作。
     * @returns 本次操作的结果；具体取值由当前状态与分支决定。
     */
    entries(): [K, V][] {
        return [...this.map.entries()];
    }

    /**
     * 淘汰最久未使用的条目（可排除受保护 key），直到 size <= maxSize。
     * @returns 被淘汰的条目列表（按淘汰顺序）
     */
    trim(...protectedKeys: K[]): [K, V][] {
        /**
         * evicted，供本模块相关规则使用。
         */
        const evicted: [K, V][] = [];
        while (this.map.size > this.maxSize) {
            /**
             * target，供本模块相关规则使用。
             */
            let target: K | null = null;
            for (const key of this.map.keys()) {
                if (!protectedKeys.includes(key)) {
                    target = key;
                    break;
                }
            }
            if (target === null) {
                break;
            }
            /**
             * value，供本模块相关规则使用。
             */
            const value = this.map.get(target) as V;
            this.map.delete(target);
            evicted.push([target, value]);
        }
        return evicted;
    }
}
